import React, { useRef } from "react";
import { useHistory } from "react-router-dom";
import { Box, Stack } from "@curves/ui-base";
import { Overview } from "@curves/ui-shared";
import useLastVisitedRow from "@Util/hook/useLastVisitedRow";
import { PageLayout } from "@Local/Components/Layout";
import { CyclePurchaseOverviewSearchBar } from "@Local/CyclePurchase/Overview";
import useCyclePurchaseOverviewApi from "@Apis/useCyclePurchaseOverviewApi";
import { getCyclePurchaseOverviewRowLabel } from "@Local/CyclePurchase/Overview/CyclePurchaseOverviewTemplate";
import { MemberOrderDetailsDialogBox } from "@Local/CyclePurchase/Overview/Dialog";
import { CYCLE_PURCHASE_DETAIL_MAIN_ROUTER_URL } from "@Router/routerPath";
import useOverview from "@Util/hook/useOverview";
import useOptions from "@Util/hook/useOptions";

const CyclePurchaseOverviewPage = () => {
	const { postCyclePurchaseOverviewApi, getCyclePurchaseOverviewOptionsApi } = useCyclePurchaseOverviewApi();
	const { options } = useOptions({
		getOptionsApi: getCyclePurchaseOverviewOptionsApi,
		isFetchOnMount: true,
	});
	const history = useHistory();
	const currentSearch = history.location?.search || ""; // 目前搜尋條件

	const { overviewData, getOverviewData, updateOverviewPage, updateOverviewSort, searchParams } = useOverview({
		getOverviewApi: postCyclePurchaseOverviewApi,
		defaultSearchParams: {
			clubID: 1,
			shippingDay: 99,
			cycleOrderType: 1,
			productName: null,
			memberSearch: null,
			sortBy: 1,
			pageIndex: 1,
			pageSize: 10,
		},
		isFetchOnMount: true,
		enableQueryString: true,
	});

	const { saveLastVisited, rowClassName } = useLastVisitedRow({
		list: overviewData?.list,
		rowKey: "cycleOrderID",
		storageKey: "lastVisitedCycleOrderId",
	});

	// 導向循環訂單明細頁，並保留當前搜尋條件
	const pushToCyclePurchaseDetail = cycleOrderID => {
		saveLastVisited(cycleOrderID);
		history.push({
			pathname: CYCLE_PURCHASE_DETAIL_MAIN_ROUTER_URL(cycleOrderID),
			search: currentSearch,
		});
	};

	const dialogRef = useRef(null);
	const headerRow = getCyclePurchaseOverviewRowLabel(pushToCyclePurchaseDetail, overviewData, dialogRef, getOverviewData);
	const showToolbar = overviewData?.list?.length > 0; // 是否顯示工具列

	// 換頁後捲回表格上方（扣除固定頂部列高度，避免表格頭被遮住）
	// 不用 smooth：載入遮罩緊接著開啟會打斷平滑動畫、直接跳到終點，故明確使用即時捲動
	const tableTopRef = useRef(null);
	const handlePageChange = page => {
		updateOverviewPage(page);
		if (!tableTopRef.current) return;
		const topBarHeight = document.querySelector(".top-bar")?.offsetHeight || 0;
		const top = tableTopRef.current.getBoundingClientRect().top + window.scrollY - topBarHeight - 16;
		window.scrollTo({ top: Math.max(top, 0) });
	};

	const contextValue = {
		overviewData,
		searchParams: { ...searchParams, sortBy: searchParams?.sortBy ?? 1 }, // 網址缺 sortBy 時回退預設排序
		getOverviewData,
		updateOverviewSort,
		updateOverviewPage: handlePageChange,
	};

	return (
		<PageLayout>
			<PageLayout.Header title="循環訂單總覽" />
			<Overview contextValue={contextValue} idKey="cycleOrderID">
				<CyclePurchaseOverviewSearchBar searchParams={searchParams} options={options} />
				<Box ref={tableTopRef} />
				{showToolbar ? (
					<Stack direction="row" alignItems="center" justifyContent="end" spacing={2} className="overview__toolbar">
						<Overview.TotalCount showRange />
						<Overview.Sort options={options.cycleOrderOrderByOptions} />
					</Stack>
				) : null}
				<Overview.Table
					headerRow={headerRow}
					cellRenderContext={{ dialogRef: null }}
					rowClassName={rowClassName}
				/>
				<Overview.Pagination className="mt-4" />
			</Overview>
			<MemberOrderDetailsDialogBox ref={dialogRef} />
		</PageLayout>
	);
};

export default CyclePurchaseOverviewPage;
